import React from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Warning as WarningIcon, Error as ErrorIcon } from '@mui/icons-material';

interface TrialStatusBannerProps {
  status: 'expired' | 'expiring';
  daysRemaining?: number;
  isAdmin: boolean;
}

const TrialStatusBanner: React.FC<TrialStatusBannerProps> = ({ status, daysRemaining, isAdmin }) => {
  const navigate = useNavigate();

  const handleUpgrade = () => {
    navigate('/settings/plans');
  };

  const isExpired = status === 'expired';
  const severity = isExpired ? 'error' : 'warning';
  const Icon = isExpired ? ErrorIcon : WarningIcon;
  
  const getMessage = () => {
    if (isExpired) {
      return {
        primary: 'Your trial has expired',
        secondary: isAdmin 
          ? 'Upgrade to continue creating simulations and ideas'
          : 'Contact your admin to upgrade'
      };
    } else {
      const dayText = daysRemaining === 1 ? 'day' : 'days';
      return {
        primary: `Trial expires in ${daysRemaining} ${dayText}`,
        secondary: isAdmin 
          ? 'Upgrade now to avoid interruption'
          : 'Contact your admin to upgrade'
      };
    }
  };

  const { primary, secondary } = getMessage();

  return (
    <Alert
      severity={severity}
      icon={<Icon />}
      sx={{ 
        borderRadius: 0,
        px: { xs: 2, sm: 3, md: 4 },
        py: 0.5,
        '& .MuiAlert-message': {
          padding: '6px 0',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }
      }}
    >
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        width: '100%',
        flexWrap: 'nowrap',
        gap: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" fontWeight="medium" sx={{ whiteSpace: 'nowrap' }}>
            {primary}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', sm: 'inline' }, whiteSpace: 'nowrap' }}>
            • {secondary}
          </Typography>
        </Box>
        
        {isAdmin && (
          <Button 
            variant="contained" 
            color={severity}
            size="small" 
            onClick={handleUpgrade}
            sx={{ 
              whiteSpace: 'nowrap',
              flexShrink: 0,
              py: 0.5,
              px: 2,
              minHeight: 'auto'
            }}
          >
            Upgrade Now
          </Button>
        )}
      </Box>
    </Alert>
  );
};

export default TrialStatusBanner;